'use client'

import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function isFocusable(el: HTMLElement): boolean {
  if (el.closest('[hidden]')) return false
  if (el.getAttribute('aria-hidden') === 'true') return false
  const style = window.getComputedStyle(el)
  return style.display !== 'none' && style.visibility !== 'hidden'
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusable)
}

/**
 * Traps keyboard focus inside a modal dialog while it is mounted.
 *
 * - Moves focus to the first focusable element (or the container) on mount
 * - Keeps Tab / Shift+Tab cycling within the container
 * - Calls `onClose` when Escape is pressed
 * - Restores focus to the previously focused element on unmount
 *
 * Attach the returned ref to the dialog's content container. For dialogs that
 * mount/unmount with open state the default `active` is enough; components
 * that stay mounted and render their dialog conditionally must pass the open
 * state as `active` so the trap arms when the dialog appears.
 */
export function useFocusTrap<T extends HTMLElement>(
  onClose?: () => void,
  active: boolean = true,
): RefObject<T | null> {
  const ref = useRef<T>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!active) return
    const container = ref.current
    if (!container) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1')
    const initial = getFocusable(container)[0] ?? container
    initial.focus()

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && onCloseRef.current) {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab' || !container) return

      const focusable = getFocusable(container)
      if (focusable.length === 0) {
        e.preventDefault()
        container.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || !container.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      previouslyFocused?.focus()
    }
  }, [active])

  return ref
}
